import {
  ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget, InlineAlertType, InlineAlertWidget
} from '@tableau/widgets';
import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Location } from '~commonInfra/utils/Location';
import { Tableau } from '~commonInfra/utils/TableauLog';
import { Messages } from '~src/codegen/Localize';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { IContentRelayPlanDetail, ISelectionQuery } from '../serverApi/types/ISelectionQuery';
import { setRelayPlanSelection } from '../utils/relayPlanActions';
import { ContentRelayPlanContentsGrid } from './ContentRelayPlanContentsGrid';
import { ContentRelayPlanDetailsStyles as Styles } from './ContentRelayPlanDetailsStyles';
import { IPlanContentRow, loadPlanContentsPage, withoutItems } from './planContentsService';

export const ContentRelayPlanContentsTabTestIds = {
  Overview: 'relay-plan-details-overview',
  Spinner: 'relay-plan-contents-spinner',
  Empty: 'relay-plan-contents-empty',
  Error: 'relay-plan-contents-error',
  Retry: 'relay-plan-contents-retry',
  Count: 'relay-plan-contents-count',
  AddContent: 'relay-plan-contents-add-content',
  RemoveSelected: 'relay-plan-contents-remove-selected',
  ShowMore: 'relay-plan-contents-show-more',
  LoadMoreError: 'relay-plan-contents-load-more-error',
  FallbackNotice: 'relay-plan-contents-fallback-notice',
};

export interface ContentRelayPlanContentsTabProps {
  readonly plan: IContentRelayPlanDetail;
  readonly urls: SpacesSiteUrls;
  /** Called after this tab changes the plan's membership, so the owner re-reads the plan. */
  readonly onPlanChanged: () => void;
}

/**
 * The Overview tab of the Content Relay plan detail page: the extraction preview — every item the plan
 * downloads — with a toolbar (count, bulk remove, Add content) and paging ("Show more").
 */
export function ContentRelayPlanContentsTab(props: ContentRelayPlanContentsTabProps): JSX.Element {
  const plan = props.plan;
  const [rows, setRows] = React.useState<ReadonlyArray<IPlanContentRow>>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [nextPageToken, setNextPageToken] = React.useState<string>(undefined);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [hasLoadMoreError, setHasLoadMoreError] = React.useState(false);
  const [selectedKeys, setSelectedKeys] = React.useState<ReadonlyArray<string>>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);
  // Set when previewExtraction is unavailable and rows are the plan's explicitly listed items only.
  const [fallback, setFallback] = React.useState<{ filterQueryCount: number }>(undefined);
  // Bumped per first-page load so a stale response (plan changed mid-flight) or an unmount is dropped.
  const loadsRef = React.useRef({ generation: 0 });

  const selectionQueries = plan.selectionQueries;
  // Reload when the membership changes, not on every plan re-read (rename / description edits).
  const selectionKey = JSON.stringify(selectionQueries || []);
  React.useEffect(function() {
    const loads = loadsRef.current;
    const generation = ++loads.generation;
    setIsLoading(true);
    setHasError(false);
    setHasLoadMoreError(false);
    loadPlanContentsPage(plan.luid, selectionQueries)
      .then(function(page) {
        if (loadsRef.current.generation !== generation) {
          return;
        }
        setRows(page.rows);
        setTotalCount(page.totalCount);
        setNextPageToken(page.nextPageToken);
        setFallback(page.isFallback ? { filterQueryCount: page.filterQueryCount || 0 } : undefined);
        // Keep checks only on rows that are still in the plan.
        setSelectedKeys(function(keys) {
          return keys.filter(function(key) { return page.rows.some(function(row) { return row.key === key; }); });
        });
        setIsLoading(false);
      })
      .catch(function(error: any) {
        if (loadsRef.current.generation !== generation) {
          return;
        }
        Tableau.Logger().info('Content Relay: failed to load plan contents: ' + error);
        setRows([]);
        setHasError(true);
        setIsLoading(false);
      });
    return function() { loads.generation++; };
    // selectionQueries is captured via selectionKey (its serialized form).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.luid, selectionKey, reloadToken]);

  const onShowMore = function(): void {
    if (!nextPageToken || isLoadingMore) {
      return;
    }
    const generation = loadsRef.current.generation;
    setIsLoadingMore(true);
    setHasLoadMoreError(false);
    loadPlanContentsPage(plan.luid, selectionQueries, nextPageToken)
      .then(function(page) {
        if (loadsRef.current.generation !== generation) {
          return;
        }
        setRows(function(current) {
          const seen: { [key: string]: boolean } = {};
          current.forEach(function(row) { seen[row.key] = true; });
          return current.concat(page.rows.filter(function(row) { return !seen[row.key]; }));
        });
        setNextPageToken(page.nextPageToken);
        setTotalCount(page.totalCount);
        setIsLoadingMore(false);
      })
      .catch(function() {
        if (loadsRef.current.generation === generation) {
          setIsLoadingMore(false);
          setHasLoadMoreError(true);
        }
      });
  };

  const onRemove = function(keys: ReadonlyArray<string>): void {
    if (isSaving || !keys.length) {
      return;
    }
    setIsSaving(true);
    setRelayPlanSelection(plan.luid, function(current: ISelectionQuery[]) { return withoutItems(current, keys); })
      .then(function(result: any) {
        if (result && result.errors && result.errors.length) {
          return Promise.reject(result);
        }
        setSelectedKeys(function(current) { return current.filter(function(key) { return keys.indexOf(key) === -1; }); });
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.relayPlanContentsRemovedToast({ COUNT: keys.length }) });
        props.onPlanChanged();
        return undefined;
      })
      .catch(function() {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.relayPlanErrorToast() });
      })
      .then(function() { setIsSaving(false); });
  };

  const addContentButton = (
    <ButtonWidget
      buttonType={ButtonType.Outline}
      onClick={function() { Location.instance.setUrl(props.urls.exploreUrl()); }}
      style={{ fontWeight: 'normal' }}
      testId={ContentRelayPlanContentsTabTestIds.AddContent}
    >
      {Messages.relayPlanContentsAddContent()}
    </ButtonWidget>
  );

  if (isLoading) {
    return (
      <div className={Styles.CenteredState}>
        <ActivitySpinnerWidget testId={ContentRelayPlanContentsTabTestIds.Spinner} spinnerSize={ActivitySpinnerSize.Large} />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={Styles.CenteredState} data-tb-test-id={ContentRelayPlanContentsTabTestIds.Error}>
        <div>{Messages.relayPlanContentsLoadError()}</div>
        <ButtonWidget
          buttonType={ButtonType.Outline}
          onClick={function() { setReloadToken(function(t) { return t + 1; }); }}
          testId={ContentRelayPlanContentsTabTestIds.Retry}
        >
          {Messages.tryAgain()}
        </ButtonWidget>
      </div>
    );
  }

  const filterQueryCount = fallback ? fallback.filterQueryCount : 0;
  // A fallback with filter queries isn't empty — the server just can't list those items yet. With no
  // listable rows at all, show only the notice: a "0 items" count and an empty grid would contradict it.
  const filterOnly = !rows.length && filterQueryCount > 0;
  if (!rows.length && !filterQueryCount) {
    return (
      <div className={Styles.CenteredState} data-tb-test-id={ContentRelayPlanContentsTabTestIds.Empty}>
        <div className={Styles.StateTitle}>{Messages.relayPlanContentsEmptyTitle()}</div>
        <div>{Messages.relayPlanContentsEmptyBody()}</div>
        {addContentButton}
      </div>
    );
  }

  // Only items the selection lists itself can be removed (see the grid's row menu).
  const removableSelected = selectedKeys.filter(function(key) {
    return rows.some(function(row) { return row.key === key && row.inSelection; });
  });

  return (
    <div className={Styles.OverviewBody} data-tb-test-id={ContentRelayPlanContentsTabTestIds.Overview}>
      <div className={Styles.Toolbar}>
        {/* Kept even when empty so the buttons stay right-aligned. */}
        <span className={Styles.ToolbarCount} data-tb-test-id={ContentRelayPlanContentsTabTestIds.Count}>
          {!filterOnly && Messages.relayPlanContentsItemCount({ COUNT: Math.max(totalCount, rows.length) })}
        </span>
        <div className={Styles.ToolbarButtons}>
          {removableSelected.length > 0 && (
            <ButtonWidget
              buttonType={ButtonType.Outline}
              onClick={function() { onRemove(removableSelected); }}
              disabled={isSaving}
              style={{ fontWeight: 'normal' }}
              testId={ContentRelayPlanContentsTabTestIds.RemoveSelected}
            >
              {Messages.relayPlanContentsRemoveSelected({ COUNT: removableSelected.length })}
            </ButtonWidget>
          )}
          {addContentButton}
        </div>
      </div>
      {fallback && (
        <InlineAlertWidget
          type={InlineAlertType.Info}
          className={Styles.Alert}
          testId={ContentRelayPlanContentsTabTestIds.FallbackNotice}
        >
          {Messages.relayPlanContentsFallbackNotice()}
          {filterQueryCount > 0 && (
            <span className={Styles.AlertHint}>{Messages.relayPlanContentsFilterQueriesNotice({ COUNT: filterQueryCount })}</span>
          )}
        </InlineAlertWidget>
      )}
      {!filterOnly && (
        <ContentRelayPlanContentsGrid
          rows={rows}
          urls={props.urls}
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          onRemove={onRemove}
        />
      )}
      {(nextPageToken || hasLoadMoreError) && (
        <div className={Styles.Footer}>
          {hasLoadMoreError && (
            <span className={Styles.ErrorText} data-tb-test-id={ContentRelayPlanContentsTabTestIds.LoadMoreError}>
              {Messages.relayPlanContentsLoadError()}
            </span>
          )}
          <ButtonWidget
            buttonType={ButtonType.LowEmphasis}
            onClick={onShowMore}
            disabled={isLoadingMore}
            showSpinner={isLoadingMore}
            testId={ContentRelayPlanContentsTabTestIds.ShowMore}
          >
            {hasLoadMoreError ? Messages.tryAgain() : Messages.relayPlanContentsShowMore()}
          </ButtonWidget>
        </div>
      )}
    </div>
  );
}
